import { LinkedInWizardData } from "@/pages/LinkedInWizard";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { FileText } from "lucide-react";

interface LinkedInWizardStep8Props {
  data: LinkedInWizardData;
  updateData: (updates: Partial<LinkedInWizardData>) => void;
  onNext: () => void;
  onPrev: () => void;
}

export function LinkedInWizardStep8({ data, updateData }: LinkedInWizardStep8Props) {
  const isSkipped = data.skipResultsAndProof || false;

  const toggleSkip = (checked: boolean) => {
    updateData({ 
      skipResultsAndProof: checked,
      // Clear results and proof if skipping
      resultsAndProof: checked ? '' : data.resultsAndProof || ''
    });
  };
  return (
    <div className="space-y-6">
      {/* Step Number and Header */}
      <div className="relative">
        <div className="absolute -top-2 -left-2">
          <div className="w-12 h-12 bg-linkedin rounded-full flex items-center justify-center shadow-linkedin">
            <span className="text-2xl font-bold text-white">8</span>
          </div>
        </div>
        <div className="ml-12">
          <h2 className="text-2xl font-bold mb-2 text-linkedin">Results & Proof</h2>
          <p className="text-linkedin/70 mb-6">
            Share your proudest accomplishments and any quantifiable results that demonstrate your value and expertise.
          </p>
        </div>
      </div>

      {/* Skip Option */}
      <Card className={`p-4 transition-all border-linkedin/20 ${
        isSkipped 
          ? 'bg-green-50 border-green-500 ring-2 ring-green-200 shadow-md' 
          : 'bg-muted/20 border-muted'
      }`}>
        <div className="flex items-start gap-3">
          <Checkbox
            id="skip-results"
            checked={isSkipped}
            onCheckedChange={toggleSkip}
            className="mt-1"
          />
          <div className="flex-1">
            <Label 
              htmlFor="skip-results" 
              className={`text-sm font-medium cursor-pointer flex items-center gap-2 ${
                isSkipped ? 'text-green-800 dark:text-green-200' : ''
              }`}
            >
              <FileText className={`w-4 h-4 ${isSkipped ? 'text-green-600' : ''}`} />
              Skip - I've already provided this information in my uploaded documents/notes
            </Label>
            <p className={`text-xs mt-1 ${
              isSkipped ? 'text-green-700 dark:text-green-300' : 'text-muted-foreground'
            }`}>
              Check this if your resume, documents, or notes already contain detailed results and achievements that can be used.
            </p>
          </div>
        </div>
      </Card>

      {isSkipped ? (
        <Card className="p-6 bg-green-50 border-green-200">
          <div className="text-center space-y-2">
            <div className="flex justify-center">
              <FileText className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="font-semibold text-green-800">
              Results & Proof Section Skipped
            </h3>
            <p className="text-sm text-green-700">
              We'll extract your achievements and results from the documents and notes you've provided earlier in the wizard.
            </p>
          </div>
        </Card>
      ) : (
        <>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="results-proof" className="text-sm font-medium text-linkedin">
                What are you most proud of accomplishing in your career? *
              </Label>
              <Textarea
                id="results-proof"
                placeholder="Share your biggest wins, achievements, metrics, awards, or recognition..."
                value={data.resultsAndProof || ""}
                onChange={(e) => updateData({ resultsAndProof: e.target.value })}
                className="min-h-[150px] border-linkedin/20 focus:ring-linkedin focus:border-linkedin"
              />
              <p className="text-xs text-linkedin/60">
                Include specific numbers, percentages, dollar amounts, team sizes, timeframes, and any awards or recognition
              </p>
            </div>
          </div>

          <Card className="p-4 bg-linkedin-light border-linkedin/20">
            <h3 className="font-semibold text-linkedin mb-3">
              Great examples to include:
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-medium text-linkedin/80 mb-2">Revenue & Growth</h4>
                <ul className="text-linkedin/70 text-sm space-y-1">
                  <li>• Grew revenue by X%</li>
                  <li>• Increased sales by $X</li>
                  <li>• Reduced costs by X%</li>
                  <li>• Improved efficiency by X%</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium text-linkedin/80 mb-2">Leadership & Scale</h4>
                <ul className="text-linkedin/70 text-sm space-y-1">
                  <li>• Managed team of X people</li>
                  <li>• Led X projects simultaneously</li>
                  <li>• Scaled operations to X users/customers</li>
                  <li>• Reduced churn by X%</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium text-linkedin/80 mb-2">Innovation & Impact</h4>
                <ul className="text-linkedin/70 text-sm space-y-1">
                  <li>• Launched new product/service</li>
                  <li>• Implemented new system/process</li>
                  <li>• Won industry awards</li>
                  <li>• Published research/articles</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium text-linkedin/80 mb-2">Time & Efficiency</h4>
                <ul className="text-linkedin/70 text-sm space-y-1">
                  <li>• Saved X hours per week/month</li>
                  <li>• Reduced project timeline by X%</li>
                  <li>• Automated X processes</li>
                  <li>• Improved response time by X%</li>
                </ul>
              </div>
            </div>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="p-4 border-linkedin/20">
              <h4 className="font-medium mb-2 text-linkedin">Strong Example</h4>
              <p className="text-sm text-linkedin/70">
                "In my role as Director, I increased qualified leads by 300% in 18 months while reducing customer acquisition cost by 45%. Led a team of 8 and launched 4 successful campaigns that generated $5M in new revenue. Won 'Professional of the Year' award from Industry Association."
              </p>
            </Card>
            <Card className="p-4 border-linkedin/20">
              <h4 className="font-medium mb-2 text-orange-600">Needs Improvement</h4>
              <p className="text-sm text-muted-foreground">
                "I've had a successful career with various achievements. I've worked on many projects that were well-received. I'm passionate about my work and always try to do my best."
              </p>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}